import { compact, isEmpty } from 'lodash';
import { LinkProps } from '@common-components';
import {
  DeclinationType,
  FormMode,
  HeraldQuoteStatus,
  QuoteFileIdType,
  QuoteSource,
  SubmissionMarketRequestStatus,
  SubmissionMethod,
  UserRole,
} from 'enums';
import { messages } from 'i18n';
import { ActivityLog, ExtendedMarketRequest } from 'types';
import { getDisplayDateWithHoursAmPm } from 'utils';
import { getDeclinationMetadataByType } from 'broker/configuration-mappers/declination-config';
import { RouteKey } from 'broker/enums';
import { useNavigate } from 'broker/hooks';
import { useWorkspaceUrls } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import useEmailActivityLink from './useEmailActivityLink';

const workspaceMessages = messages.submissionWorkspace.marketRequestsWorkspace;

const getDeclinedEmptySlot = (marketRequest: ExtendedMarketRequest) => {
  const title = compact(
    marketRequest.declination?.declinationReasons.map((declinationReason) =>
      declinationReason.type === DeclinationType.OTHER
        ? declinationReason.details
        : getDeclinationMetadataByType(declinationReason.type)!.reason,
    ),
  ).join(', ');

  const subtitle = workspaceMessages.declinedEmptySlot(getDisplayDateWithHoursAmPm(marketRequest.updatedAt));

  return {
    title,
    subtitle,
    isAIExtracted: marketRequest.declination?.isAIExtracted,
  };
};

export const useStatusSpecificMetadata = (
  marketRequest: ExtendedMarketRequest,
  emailActivity?: ActivityLog,
):
  | {
      title: string;
      subtitle: string;
      isAIExtracted?: boolean;
      linkProps?: Omit<LinkProps, 'children'> & { linkText?: string; linkTooltip?: string };
      titleTextAfterLink?: string;
    }
  | undefined => {
  const navigate = useNavigate();
  const { partialSubmission } = useSubmissionsWorkspace();
  const { binderUrl, policyUrl } = useWorkspaceUrls();
  const emailActivityLinkProps = useEmailActivityLink({ emailActivity });
  const timestampSubtitle = getDisplayDateWithHoursAmPm(marketRequest.updatedAt);

  if (marketRequest.status === SubmissionMarketRequestStatus.Declined) {
    return getDeclinedEmptySlot(marketRequest);
  }

  if (
    marketRequest.status === SubmissionMarketRequestStatus.PolicyIssued &&
    marketRequest.quote?.policySource === QuoteSource.Email &&
    !marketRequest.quote?.policyDetails?.manuallyReviewed
  ) {
    return {
      title: workspaceMessages.continuePolicyEditingTitle(marketRequest.quote.policySource),
      subtitle: timestampSubtitle,
      linkProps: {
        onClick: () =>
          navigate(policyUrl, {
            routeKey: RouteKey.Quote,
            state: {
              selectedMarket: marketRequest.market,
              selectedQuote: marketRequest.quote,
              quoteFileIds: marketRequest.quote?.policyFileIds,
              fileType: QuoteFileIdType.Policy,
              mode: FormMode.edit,
              insuranceProductId: marketRequest.insuranceProduct?.id,
            },
          }),
        linkText: workspaceMessages.continueQuoteEditingLinkText,
      },
      titleTextAfterLink: workspaceMessages.continuePolicyIssuingSuffix,
    };
  }

  if (
    marketRequest.status === SubmissionMarketRequestStatus.Bound &&
    marketRequest.quote &&
    isEmpty(marketRequest.quote.flowBinderFileIds)
  ) {
    return {
      title: workspaceMessages.continueBinderEditingTitle(marketRequest.quote.binderSource),
      subtitle: timestampSubtitle,
      linkProps: {
        onClick: () =>
          navigate(binderUrl, {
            routeKey: RouteKey.Quote,
            state: {
              selectedMarket: marketRequest.market,
              selectedQuote: marketRequest.quote,
              quoteFileIds: marketRequest.quote?.binderFileIds,
              fileType: QuoteFileIdType.Binder,
              mode: FormMode.edit,
              insuranceProductId: marketRequest.insuranceProduct?.id,
            },
          }),
        linkText: workspaceMessages.continueQuoteEditingLinkText,
      },
      titleTextAfterLink: workspaceMessages.continueBinderEditingLinkSuffix,
    };
  }

  const { awaitingQuoteEmptySlot, readyToMarketEmptySlot, methods, awaitingQuoteReferral, awaitingQuoteReferred } =
    workspaceMessages;

  if (
    [SubmissionMarketRequestStatus.AwaitingQuote, SubmissionMarketRequestStatus.ReadyToMarket].includes(
      marketRequest.status,
    )
  ) {
    const { viaApi, openPortal } = methods;
    const isIncumbent = !isEmpty(marketRequest.incumbentInfo);
    const marketRequestCreatedByFastTrack =
      partialSubmission?.isFastTrack && marketRequest.createdByUserDetails?.role === UserRole.Machine;
    const title =
      marketRequest.status === SubmissionMarketRequestStatus.AwaitingQuote
        ? awaitingQuoteEmptySlot(marketRequestCreatedByFastTrack)
        : readyToMarketEmptySlot.addedToSubmission(marketRequestCreatedByFastTrack, isIncumbent);

    const getTitleForSubmitViaApiMetadata = () => {
      switch (marketRequest.heraldQuoteStatus) {
        case HeraldQuoteStatus.Referred:
          return awaitingQuoteReferred;
        case HeraldQuoteStatus.Referral:
          return awaitingQuoteReferral;
        default:
          return `${title} ${viaApi}`;
      }
    };

    switch (marketRequest.submissionMethod) {
      case SubmissionMethod.API:
        return {
          title: getTitleForSubmitViaApiMetadata(),
          subtitle: timestampSubtitle,
        };
      case SubmissionMethod.Portal:
        return {
          title,
          subtitle: timestampSubtitle,
          linkProps: {
            href: marketRequest.insuranceProduct?.submissionPortal,
            target: '_blank',
            linkText: openPortal,
            linkTooltip: marketRequest.insuranceProduct?.submissionPortal,
            ...emailActivityLinkProps,
          },
        };
      case SubmissionMethod.Email:
        return {
          title,
          subtitle: timestampSubtitle,
          linkProps: {
            ...emailActivityLinkProps,
          },
        };
      default:
        return { title, subtitle: timestampSubtitle };
    }
  }

  return undefined;
};
